import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, MapPin, CalendarDays, Globe2, Sparkles, ArrowUpRight } from "lucide-react";
import { PageHeader, Spinner, Pill } from "../components/common";
import { GeoExplorer } from "../components/GeoExplorer";
import CuratedRail from "../components/travel/CuratedRail";
import { pickAirportBg, CITY_SEED_PHOTOS } from "../lib/assets";
import { SITE } from "../lib/site";
import api from "../lib/api";
import SEOHead from "../components/SEOHead";
import { breadcrumbLd } from "../lib/structuredData";

function destinationImage(d) {
  return (CITY_SEED_PHOTOS && CITY_SEED_PHOTOS[d.name]) || pickAirportBg(d.name);
}

export default function Destinations() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("");
  const [placeMatches, setPlaceMatches] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/destinations").then((r) => setData(r.data)).catch(() => {});
    document.title = `World Destinations — Guides, Attractions & Transport | ${SITE.brandDomain}`;
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    let items = data.items;
    if (region) items = items.filter((d) => d.region === region);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      items = items.filter((d) => d.name.toLowerCase().includes(s) || d.country.toLowerCase().includes(s) || d.tagline.toLowerCase().includes(s));
    }
    return items;
  }, [data, q, region]);

  // Auto-crawl: when no curated destination matches, look the place up in the
  // global geo index (229k+ places) and offer to open/generate its guide.
  useEffect(() => {
    if (!data || filtered.length > 0 || q.trim().length < 3) { setPlaceMatches([]); return; }
    const t = setTimeout(() => {
      api.get("/search", { params: { q: q.trim() } })
        .then((r) => setPlaceMatches(r.data.places || []))
        .catch(() => setPlaceMatches([]));
    }, 350);
    return () => clearTimeout(t);
  }, [data, filtered.length, q]);

  return (
    <div data-testid="destinations-page">
      <SEOHead
        title={`World Destinations — Guides, Attractions & Transport`}
        description="Flagship destination guides covering attractions, events, visas and every mode of transportation worldwide."
        canonical="/destinations"
        jsonLd={breadcrumbLd([["Home", "/"], ["Destinations"]])}
      />
      <PageHeader eyebrow={`${SITE.name} · Explore the World by Place`} title="Destinations" entityCode="WORLD"
        subtitle="Flagship destination guides connecting attractions, events, visas and every mode of transportation — begin with a place, and we connect the rest of the journey." />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <GeoExplorer />
        <CuratedRail items={data?.items || []} />
        <div className="flex flex-wrap gap-3 mb-8">
          <div className="relative flex-1 min-w-[240px] max-w-xl flex">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="destinations-search"
              placeholder="Search any place — city, town, country…"
              className="w-full border border-zinc-300 bg-white pl-9 pr-3 py-2.5 text-sm focus:outline-none" />
            <button onClick={() => placeMatches[0] && filtered.length === 0 ? nav(`/explore/${placeMatches[0].slug}`) : null}
              data-testid="destinations-search-go"
              className="shrink-0 px-4 bg-[#0e7490] text-white text-sm font-800 hover:opacity-90">Go&nbsp;&rsaquo;</button>
          </div>
          <select value={region} onChange={(e) => setRegion(e.target.value)} data-testid="destinations-region-filter"
            className="border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:outline-none">
            <option value="">All regions</option>
            {(data?.regions || []).map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        {!data ? <Spinner /> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((d) => (
              <Link key={d.id} to={`/destinations/${d.id}`} data-testid={`destination-card-${d.id}`}
                className="group relative rounded-2xl overflow-hidden shadow-md hover:shadow-xl hover:-translate-y-1 transition-all border border-amber-100/50 bg-white flex flex-col">
                {/* Full-bleed cinematic photo */}
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img src={destinationImage(d)} alt={d.name} loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                  {/* Corner pills — country top-left, region top-right */}
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[10px] font-mono uppercase tracking-[0.18em] text-slate-800 shadow-sm">
                    <MapPin className="w-3 h-3 text-amber-700" /> {d.country}
                  </div>
                  {d.region && (
                    <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-amber-500/95 backdrop-blur text-[10px] font-mono uppercase tracking-[0.18em] text-white shadow-sm">
                      {d.region}
                    </div>
                  )}
                  {/* City name in giant serif over the photo */}
                  <div className="absolute inset-x-0 bottom-0 p-5">
                    <div className="font-serif font-800 text-3xl sm:text-4xl text-white leading-[1.05] tracking-tight drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
                      {d.name}
                    </div>
                    {d.tagline && (
                      <div className="mt-1 text-[13px] italic text-amber-200 font-500 line-clamp-1">{d.tagline}</div>
                    )}
                  </div>
                </div>
                {/* Cream editorial footer */}
                <div className="p-4 flex-1 flex flex-col" style={{ background: "linear-gradient(180deg, #faf7f0 0%, #ffffff 100%)" }}>
                  <p className="text-sm text-slate-700 leading-relaxed line-clamp-2">{d.description}</p>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-amber-100">
                    <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500 inline-flex items-center gap-1">
                      <CalendarDays className="w-3 h-3 text-amber-700" /> Best: {d.best_time}
                    </span>
                    <span className="inline-flex items-center gap-0.5 text-amber-700 text-xs font-800 group-hover:gap-1.5 transition-all">
                      Explore <ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full" data-testid="destinations-no-match">
                <p className="text-sm text-muted-foreground mb-3">No curated destination matches "{q}" — but we can still take you there:</p>
                {placeMatches.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {placeMatches.map((p) => (
                      <Link key={p.slug} to={`/explore/${p.slug}`} data-testid={`destination-place-${p.slug.split("/").pop()}`}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1a73e8] text-white text-sm font-800 hover:opacity-90">
                        <Globe2 className="w-4 h-4" /> Open the {p.name} travel guide
                        <span className="text-[10px] font-400 text-blue-100">{[p.state !== p.name ? p.state : null, p.country].filter(Boolean).join(", ")}</span>
                      </Link>
                    ))}
                  </div>
                ) : q.trim().length >= 3 ? (
                  <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4 text-[#FBBC05]" /> Searching our global index of 229,000+ places…
                    If nothing appears, try the country picker above — a guide will be generated automatically.
                  </p>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
